import { format, isToday } from 'date-fns';
import { Check } from 'lucide-react';
import { Card } from '../common/Card';
import { StatusBadge } from '../common/StatusBadge';
import { useJobStore } from '../../stores/jobStore';
import type { Job, JobRun } from '../../types/jobs';

interface JobStatusBoardProps {
  jobs: Job[];
  recentRuns: JobRun[];
}

function formatDuration(ms: number | null): string {
  if (ms === null || ms === 0) return '--';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;
  return `${hours}h ${remainMinutes}m`;
}

function formatTime(dateStr: string): string {
  return format(new Date(dateStr), 'h:mm a');
}

function getIntervalLabel(expr: string): string {
  const minutes = parseInt(expr, 10);
  if (isNaN(minutes)) return expr;
  if (minutes < 60) return `Every ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remain = minutes % 60;
  return remain > 0 ? `Every ${hours}h ${remain}m` : `Every ${hours}h`;
}

export function JobStatusBoard({ jobs, recentRuns }: JobStatusBoardProps) {
  const acknowledgeWarning = useJobStore((s) => s.acknowledgeWarning);
  const intervalJobs = jobs.filter((j) => j.schedule_type === 'interval' && j.enabled);
  const cronJobs = jobs.filter((j) => j.schedule_type === 'cron' && j.enabled);

  const scheduledToday = cronJobs.filter((j) => {
    if (!j.next_run) return false;
    return isToday(new Date(j.next_run));
  });

  const completedToday = recentRuns
    .filter((r) => {
      if (!r.ended_at) return false;
      return isToday(new Date(r.started_at)) && (r.status === 'success' || r.status === 'error' || r.status === 'warning');
    })
    .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());

  const jobNameMap = new Map(jobs.map((j) => [j.id, j.name]));

  return (
    <Card className="flex-1">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-text-primary">Job Status</h3>
        <p className="text-[10px] text-text-muted mt-0.5">Job-level view — grouped by schedule type</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Always Running */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h4 className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Always Running
            </h4>
            <span className="text-xs text-text-muted">({intervalJobs.length})</span>
          </div>
          <div className="space-y-2">
            {intervalJobs.map((job) => (
              <div
                key={job.id}
                className="bg-bg-tertiary rounded-md p-2.5 border border-border-subtle"
              >
                <p className="text-sm font-medium text-text-primary truncate">{job.name}</p>
                <p className="text-xs text-text-muted mt-1">
                  {getIntervalLabel(job.schedule_expr)}
                </p>
                {job.last_run ? (
                  <p className="text-[10px] text-text-muted mt-0.5">
                    Last: {formatTime(job.last_run.started_at)}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* Scheduled Today */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h4 className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Scheduled Today
            </h4>
            <span className="text-xs text-text-muted">({scheduledToday.length})</span>
          </div>
          <div className="space-y-2">
            {scheduledToday.map((job) => (
              <div
                key={job.id}
                className="bg-bg-tertiary rounded-md p-2.5 border border-border-subtle"
              >
                <p className="text-sm font-medium text-text-primary truncate">{job.name}</p>
                {job.next_run ? (
                  <p className="text-xs text-text-muted mt-1">
                    Next: {formatTime(job.next_run)}
                  </p>
                ) : null}
              </div>
            ))}
            {scheduledToday.length === 0 ? (
              <p className="text-xs text-text-muted py-4 text-center">No upcoming runs</p>
            ) : null}
          </div>
        </div>

        {/* Completed Today */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h4 className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Completed Today
            </h4>
            <span className="text-xs text-text-muted">({completedToday.length})</span>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {completedToday.map((run) => (
              <div
                key={run.id}
                className={`bg-bg-tertiary rounded-md p-2.5 border border-border-subtle ${
                  run.acknowledged ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-text-primary truncate flex-1">
                    {jobNameMap.get(run.job_id) ?? run.job_id}
                  </p>
                  <div className="flex items-center gap-1.5">
                    {run.status === 'warning' && !run.acknowledged ? (
                      <button
                        onClick={() => acknowledgeWarning(run.id, run.job_id)}
                        className="text-text-muted hover:text-success transition-colors p-0.5 rounded"
                        title="Acknowledge warning"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                    {run.acknowledged ? (
                      <span className="text-[9px] text-text-muted uppercase tracking-wider">Ack'd</span>
                    ) : null}
                    <StatusBadge status={run.status} size="sm" />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-text-muted">
                    {formatTime(run.started_at)}
                  </span>
                  <span className="text-[10px] text-text-muted">
                    {formatDuration(run.duration_ms)}
                  </span>
                </div>
              </div>
            ))}
            {completedToday.length === 0 ? (
              <p className="text-xs text-text-muted py-4 text-center">No completed runs yet</p>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default JobStatusBoard;
